import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Send, Inbox, Edit3, Users, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

function MessagingCenter({ userId, userName }) {
  const [messages, setMessages] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [newMessage, setNewMessage] = useState({ to: '', subject: '', content: '', type: 'user' });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('inbox'); // 'inbox', 'compose'

  useEffect(() => {
    if (userId) {
      fetchMessages();
      // Set up polling for new messages
      const interval = setInterval(fetchMessages, 30000); // 30 seconds
      return () => clearInterval(interval);
    }
  }, [userId]);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/inbox/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to fetch messages');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_user_id: userId,
          to_user_id: newMessage.type === 'admin' ? null : newMessage.to,
          to_admin: newMessage.type === 'admin',
          subject: newMessage.subject,
          content: newMessage.content,
          message_type: 'direct'
        })
      });

      if (!response.ok) throw new Error('Failed to send message');

      toast.success('Message sent successfully!');
      setNewMessage({ to: '', subject: '', content: '', type: 'user' });
      setView('inbox');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/${messageId}/read`, {
        method: 'PUT'
      });
      fetchMessages();
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const unreadCount = messages.filter(m => !m.read && !m.is_sender).length;

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Messages</h2>
          <p className="text-gray-600">Connect with fellow learners and the teaching team</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setView('inbox')}
            variant={view === 'inbox' ? 'default' : 'outline'}
            className={view === 'inbox' ? 'bg-blue-600 text-white' : 'border-black'}
            style={{ borderRadius: 0 }}
          >
            <Inbox className="w-4 h-4 mr-2" />
            Inbox {unreadCount > 0 && <Badge className="ml-2 bg-red-500 text-white">{unreadCount}</Badge>}
          </Button>
          <Button
            onClick={() => setView('compose')}
            variant={view === 'compose' ? 'default' : 'outline'}
            className={view === 'compose' ? 'bg-green-600 text-white' : 'border-black'}
            style={{ borderRadius: 0 }}
          >
            <Edit3 className="w-4 h-4 mr-2" />
            Compose
          </Button>
        </div>
      </div>

      {view === 'inbox' && (
        <div className="space-y-4">
          {messages.length === 0 ? (
            <Card style={{ borderRadius: 0 }}>
              <CardContent className="p-12 text-center text-gray-500">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-xl mb-2">No messages yet</p>
                <p>Start connecting with fellow learners or send a message to the teaching team!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => (
                <Card 
                  key={message.id} 
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    !message.read && !message.is_sender ? 'border-l-4 border-blue-600 bg-blue-50' : ''
                  }`}
                  style={{ borderRadius: 0 }}
                  onClick={() => {
                    setActiveConversation(message);
                    if (!message.read && !message.is_sender) {
                      markAsRead(message.id);
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-bold text-gray-800">
                            {message.to_admin ? 'To: Teaching Team' : 
                             message.is_sender ? `To: ${message.to_name || 'User'}` : 
                             `From: ${message.from_name}`}
                          </p>
                          {!message.read && !message.is_sender && (
                            <Badge className="bg-blue-500 text-white text-xs">NEW</Badge>
                          )}
                          {message.is_sender && (
                            <Badge className="bg-gray-500 text-white text-xs">SENT</Badge>
                          )}
                        </div>
                        <p className="text-lg font-semibold text-gray-700 mb-2">{message.subject}</p>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {message.content.substring(0, 150)}...
                        </p>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(message.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'compose' && (
        <Card style={{ borderRadius: 0 }}>
          <CardHeader>
            <CardTitle className="text-2xl">Compose Message</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={sendMessage} className="space-y-4">
              <div className="space-y-3">
                <label className="block text-sm font-bold">Send To:</label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => setNewMessage(prev => ({ ...prev, type: 'admin' }))}
                    variant={newMessage.type === 'admin' ? 'default' : 'outline'}
                    className={newMessage.type === 'admin' ? 'bg-purple-600 text-white' : 'border-black'}
                    style={{ borderRadius: 0 }}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Teaching Team
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setNewMessage(prev => ({ ...prev, type: 'user' }))}
                    variant={newMessage.type === 'user' ? 'default' : 'outline'}
                    className={newMessage.type === 'user' ? 'bg-blue-600 text-white' : 'border-black'}
                    style={{ borderRadius: 0 }}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Fellow Learner
                  </Button>
                </div>
              </div>

              {newMessage.type === 'user' && (
                <div className="space-y-2">
                  <label className="block text-sm font-bold">To User ID:</label>
                  <Input
                    placeholder="Enter user ID (ask them to share their ID)"
                    value={newMessage.to}
                    onChange={(e) => setNewMessage(prev => ({ ...prev, to: e.target.value }))}
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  />
                  <p className="text-xs text-gray-600">
                    Note: User ID sharing feature coming soon. For now, ask fellow learners for their ID.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-bold">Subject:</label>
                <Input
                  placeholder="What's this about?"
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage(prev => ({ ...prev, subject: e.target.value }))}
                  className="border-black"
                  style={{ borderRadius: 0 }}
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-bold">Message:</label>
                <Textarea
                  placeholder="Share your thoughts, questions, or reflections..."
                  value={newMessage.content}
                  onChange={(e) => setNewMessage(prev => ({ ...prev, content: e.target.value }))}
                  className="border-black min-h-[150px]"
                  style={{ borderRadius: 0 }}
                  required
                />
              </div>

              <Button 
                type="submit"
                className="w-full bg-black text-white hover:bg-gray-800 py-4"
                style={{ borderRadius: 0 }}
                disabled={loading || !newMessage.subject || !newMessage.content}
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Active Conversation Modal */}
      {activeConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-96 overflow-y-auto" style={{ borderRadius: 0 }}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl">{activeConversation.subject}</CardTitle>
                  <p className="text-sm text-gray-600">
                    {activeConversation.is_sender ? 'To: ' : 'From: '}
                    {activeConversation.to_admin ? 'Teaching Team' : activeConversation.from_name}
                  </p>
                </div>
                <Button 
                  onClick={() => setActiveConversation(null)}
                  variant="outline"
                  className="border-gray-400"
                  style={{ borderRadius: 0 }}
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-wrap text-gray-700 p-4 bg-gray-50">
                {activeConversation.content}
              </div>
              <div className="mt-4 text-xs text-gray-500">
                {new Date(activeConversation.created_at).toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}

export default MessagingCenter;